import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import Switch from "antd/es/switch";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { Button, ButtonProps } from "@mapmycustomers/ui";

import { useMap } from "@app/component/map/FeaturedMap";
import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import { MIN_LEGEND_HEIGHT } from "@app/scene/map/utils/consts";
import mapSettings from "@app/scene/map/utils/mapSettings";

import styles from "./LegendCard.module.scss";

export interface BaseLegendCardProps {
  active?: boolean;
  className?: string;
  defaultHeight?: number;
  expanded?: boolean;
  isLayer?: boolean;
  onHeightChange?: (height: number) => void;
  onResize?: (resizing: boolean) => void;
  onToggle?: (expanded: boolean) => void;
  visible?: boolean;
}

interface ManageButtonProps extends ButtonProps {
  tooltip?: ReactNode;
}

export interface LegendCardProps extends BaseLegendCardProps {
  children?: ReactNode;
  icon: ReactNode;
  manageable?: boolean;
  manageButtonProps?: Partial<ManageButtonProps>;
  onManage?: () => void;
  onVisibilityChange?: (visible: boolean) => void;
  title: ReactNode;
}

const LegendCard: React.FC<LegendCardProps> = ({
  active,
  children,
  className,
  defaultHeight = MIN_LEGEND_HEIGHT,
  expanded = true,
  icon,
  isLayer,
  manageable,
  manageButtonProps,
  onHeightChange,
  onManage,
  onResize,
  onToggle,
  onVisibilityChange,
  title,
  visible = false,
}) => {
  const intl = useIntl();

  const { map } = useMap();
  const refBox = useRef<HTMLDivElement | null>(null);
  const refTop = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const container = map.getDiv();
    const mapHeight = container.clientHeight;
    const maxHeightAllowed = (75 / 100) * mapHeight;
    const resizableElement = refBox.current;
    if (resizableElement === null) {
      return;
    }

    resizableElement.style.height = `${defaultHeight}px`;
    let height = defaultHeight;
    let lastYCoordinate = 0;

    const handleMouseMoveTopResize = (event: MouseEvent | TouchEvent) => {
      let dy = 0;
      if (event.type === "mousemove") {
        dy = (event as MouseEvent).clientY - lastYCoordinate;
        lastYCoordinate = (event as MouseEvent).clientY;
      } else {
        dy = (event as TouchEvent).touches[0].clientY - lastYCoordinate;
        lastYCoordinate = (event as TouchEvent).touches[0].clientY;
      }

      height = Math.max(MIN_LEGEND_HEIGHT, Math.min(height - dy, maxHeightAllowed));
      resizableElement.style.height = `${height}px`;
      mapSettings.setKeyToolbarHeight(height);
      onHeightChange?.(height);
    };

    const handleMouseUpTopResize = () => {
      resizableElement.classList.remove(styles.resizing);
      onResize?.(false);
      document.removeEventListener("mousemove", handleMouseMoveTopResize);
      document.removeEventListener("touchmove", handleMouseMoveTopResize);
    };

    const handleMouseDownTopresize = (event: MouseEvent | TouchEvent) => {
      event.preventDefault();
      resizableElement.classList.add(styles.resizing);
      onResize?.(true);
      if (event.type === "mousedown") {
        lastYCoordinate = (event as MouseEvent).clientY;
        document.addEventListener("mousemove", handleMouseMoveTopResize);
        document.addEventListener("mouseup", handleMouseUpTopResize);
      } else {
        lastYCoordinate = (event as TouchEvent).touches[0].clientY;
        document.addEventListener("touchmove", handleMouseMoveTopResize);
        document.addEventListener("touchend", handleMouseUpTopResize);
      }
    };

    const resizerTop = refTop.current;
    resizerTop?.addEventListener("mousedown", handleMouseDownTopresize);
    resizerTop?.addEventListener("touchstart", handleMouseDownTopresize);

    return () => {
      resizerTop?.removeEventListener("mousedown", handleMouseDownTopresize);
      resizerTop?.removeEventListener("touchstart", handleMouseDownTopresize);
    };
  }, [defaultHeight, map, onHeightChange, onResize]);

  const [localVisibility, setLocalVisibility] = useState(visible);
  useEffect(() => {
    setLocalVisibility(visible);
  }, [visible]);

  const handleVisibilityChange = useCallback(
    (visible) => {
      setLocalVisibility(visible);
      // delay actual level toggling letting switch animation to complete first
      // purely for better UX, no other reason
      setTimeout(() => {
        onVisibilityChange?.(visible);
      }, 200);
    },
    [onVisibilityChange]
  );

  const { tooltip, ...otherManageButtonProps } = manageButtonProps ?? {};

  return expanded ? (
    <Interactive>
      <div className={cn(styles.container, className)} ref={refBox}>
        <div className={styles.resizer}>
          <div className={styles.resizerTop} ref={refTop}></div>
        </div>
        <div className={styles.fullContent}>
          <div className={styles.header}>
            <div className={styles.icon}>{icon}</div>
            <div className={styles.title}>{title}</div>
            <div className={styles.actions}>
              {manageable && (
                <Tooltip title={tooltip}>
                  {/* this div is needed. Otherwise Tooltip "steals" className from the button and applies it on another element */}
                  <div>
                    <Button
                      {...otherManageButtonProps}
                      className={styles.manage}
                      onClick={manageable ? onManage : undefined}
                      type="link"
                    >
                      {intl.formatMessage({
                        id: "map.legends.pinLegend.settings",
                        defaultMessage: "Manage",
                        description: "Button for managing pin Legend settings",
                      })}
                    </Button>
                  </div>
                </Tooltip>
              )}
              {isLayer && (
                <Switch
                  checked={localVisibility}
                  onChange={handleVisibilityChange}
                  size="default"
                />
              )}
            </div>
          </div>
          <div className={styles.content}>{children}</div>
        </div>
      </div>
    </Interactive>
  ) : (
    <Interactive className={styles.legendItems}>
      <Tooltip
        overlayClassName={styles.tooltip}
        title={
          active
            ? intl.formatMessage({
                id: "map.legends.active.close.tooltip",
                defaultMessage: "Close",
                description: "Tooltip of the legend to allow users to collapse the active window",
              })
            : title
        }
      >
        <div
          className={cn(styles.containerCollapsed, {
            [styles.active]: active,
            [styles.disabled]: !active && !visible,
            [styles.enabled]: !active && visible,
          })}
          onClick={() => onToggle?.(true)}
        >
          {icon}
        </div>
      </Tooltip>
    </Interactive>
  );
};

export default LegendCard;
